// Portal Order API Service - endpoints for salesperson order review

import api from './api';
import type {
  DraftParkReason,
  OrderRecord,
  OrderLineItem,
  OrderStateTransition,
  OrderTimelineResponse,
  LatestEventsResponse,
  LineItemCreateRequest,
  PortalManualOrderRequest,
  ReviewResolution,
} from '../types/orders';
import type { InvoiceDetail } from '../types/invoices';

// Portal-specific response types

/**
 * The one customer every order matching the current search belongs to
 * (kanban f33d0f2c). Present only for a one-word search whose matches all
 * share a customer number; the page offers a link to that customer's page.
 */
export interface MatchedCustomer {
  customer_no: string;
  customer_name: string;
  slug: string | null;
}

export interface PortalOrderListResponse {
  orders: OrderRecord[];
  total: number;
  limit: number;
  offset: number;
  matched_customer?: MatchedCustomer | null;
}

export interface SubmissionFieldError {
  field: string;
  message: string;
  current_value?: string | null;
  line_item_position?: number | null;
}

export interface SageSubmissionFeedback {
  success: boolean;
  sage_order_no?: string | null;
  error_message?: string | null;
  latency_ms?: number;
  submission_field_errors?: SubmissionFieldError[] | null;
  // Soft ship-to "address doesn't match" advisories. When
  // requires_acknowledgment is true the order was NOT sent — show the warning
  // and offer "Submit anyway" (retry with acknowledge_warnings=true).
  requires_acknowledgment?: boolean;
  submission_warnings?: SubmissionFieldError[] | null;
}

export interface SageProcessingMessage {
  message: string;
  severity: 'warning' | 'error';
}

/** Mirrors SageFreightInfo in app/routes/portal_orders.py. */
export interface SageFreightInfo {
  // Sage-computed SO_SalesOrderHeader.FreightAmt — the authoritative freight
  // cost after submission (the quote modal is estimates-only). Serialized as
  // a decimal string by the API.
  freight_amt: string | number | null;
  // Sage's current carrier; can diverge from orders.ship_via after a
  // Sage-side CS edit.
  ship_via: string | null;
  // Sage's current fulfilment warehouse (SO_SalesOrderHeader.WarehouseCode);
  // diverges from orders.warehouse_code once the desk re-points the SO.
  warehouse_code?: string | null;
  // Sage's current bill-of-lading term ("ppd." / "col." / "3rd party");
  // diverges from orders.udf_bl after a Sage-side desk edit, so the
  // Edit-in-Sage modal seeds its Bill of Lading field from here.
  udf_bl?: string | null;
}

/** Mirrors FreightErrorEventRead in db/models/freight_errors.py. */
export interface FreightErrorInfo {
  id: string;
  sales_order_no: string;
  status: 'detected' | 'notified' | 'resolved' | 'dismissed';
  ship_via: string | null;
  tracking_number: string | null;
  detected_at: string;
  email_received_at: string | null;
  resolved_at: string | null;
  resolution_source: string | null;
}

/** Mirrors FreightNeedsInputRead in app/schemas/bol_scan_trigger.py.
 *  The portal-triggered BOL scan came back needs_input asking for freight:
 *  the sales site is holding the ship until a CSR keys the amount. */
export interface FreightNeedsInputInfo {
  trigger_id: string;
  sage_order_no: string;
  ship_via: string | null;
  needs: string[];
  needs_reason: string | null;
  needs_detail: string | null;
  created_at: string;
  /** Ready-made sentence: "the live SAIA rate lookup failed (...)". */
  why: string;
}

/** Mirrors BolScanDeskAskRead in app/schemas/bol_scan_trigger.py (kanban cee85483).
 *  A scanned BOL waiting on the desk for something other than freight: the
 *  sales site wants a PRO / pickup number, or the portal held the page because
 *  the order's ship date is outside the window until someone confirms it left. */
export interface BolScanDeskAskInfo {
  trigger_id: string;
  sage_order_no: string;
  ship_via: string | null;
  ask: 'tracking_no' | 'ship_confirmation';
  /**
   * Why a ship_confirmation is being asked: 'outside_ship_window' (ship date
   * outside the guard window) or 'content_link' (the page was matched by its
   * contents, not an order barcode). Null on a tracking_no ask.
   */
  needs_reason?: string | null;
  est_ship_date: string | null;
  created_at: string;
  /** Ready-made sentence: "the sales site needs a PRO or pickup number ...". */
  why: string;
}

/** Body of POST /portal/orders/{id}/bol-scan/answer. */
export type BolScanDeskAnswer =
  | { action: 'tracking_no'; tracking_no: string }
  | { action: 'skip_tracking' }
  | { action: 'confirm_shipped' };

/** Mirrors BolScanResubmitRead — what the automatic re-call did after a save. */
export interface BolScanResubmitInfo {
  trigger_id: string;
  parent_trigger_id: string;
  sage_order_no: string;
  status: 'processed' | 'already_processed' | 'needs_input' | 'failed' | string;
  needs: string[];
  error: string | null;
  message: string;
}

export interface PortalOrderDetailResponse {
  order: OrderRecord;
  line_items: OrderLineItem[];
  history: OrderStateTransition[];
  allowed_transitions: string[];
  source_email_id: string | null;
  source_email_subject: string | null;
  source_email_body: string | null;
  source_email_body_html: string | null;
  source_email_from: string | null;
  sage_submission?: SageSubmissionFeedback | null;
  sage_processing_messages?: SageProcessingMessage[] | null;
  // Most-recent Sage invoice for this order (kit-component and comment lines
  // hidden server-side). Null until the order has been invoiced.
  invoice?: InvoiceDetail | null;
  // True iff the order still exists as an OPEN sales order in Sage (not yet
  // invoiced/shipped). Gates the "Edit in Sage" affordance on the order modal.
  editable_in_sage?: boolean;
  // Same open-in-Sage fact WITHOUT the Edit-in-Sage feature flag folded in,
  // so the "Confirm in Sage" dialog can tell "already in Sage" from "not in
  // Sage" (and ask for the AU# in the latter case).
  open_in_sage?: boolean;
  // Cloud->Sage writes for this order NOT yet confirmed landed (status
  // pending/submitted). Rendered as a "pushed - awaiting Sage confirmation"
  // banner (current->pushed from each write's `changes`) until the sage_writes
  // reconciler confirms; empties on confirm, or the write turns `failed`.
  pending_sage_writes?: SageWriteAuditEntry[];
  // Merlin-proposed edits from change-order emails awaiting the CSR's
  // confirm/dismiss (FSM 'proposed' only; only while Edit-in-Sage is enabled).
  edit_proposals?: OrderEditProposalRead[];
  // Sage-computed freight while the order is still open in Sage. Null for
  // drafts, never-landed orders, and invoiced orders (invoice.freight_amt
  // takes over once invoiced).
  sage_freight?: SageFreightInfo | null;
  // Open sales-site "Freight Error" notice: carrier quote failed at BOL
  // scan, invoice built with $0 freight — CSR must key freight by hand.
  freight_error?: FreightErrorInfo | null;
  // Sage customer-master order notes (AR_Customer.UDF_ORDER_NOTES): standing
  // handling/carrier instructions ops maintains (e.g. "SHIP R&L ACCOUNT
  // IMI144", "DO NOT SHIP SAIA"), historically only visible once the PO
  // printed from Sage. Null when the customer has no notes.
  customer_order_notes?: string | null;
  // Truckload freight amount keyed on the order (order_freight_presets):
  // freight + fuel surcharge the broker quoted. Null when never set.
  truckload_freight?: TruckloadFreightSummary | null;
  // True when the Freight row may take a keyed amount: a truckload/logistics
  // carrier (Adam's needsShippingLogistics list), OR a BOL scan waiting for
  // freight on this order (freight_needs_input), OR an open sales-site
  // "Freight Error" notice on it (freight_error) — whatever the carrier.
  truckload_freight_eligible?: boolean;
  // ship_via is on the truckload/logistics list (no live rate: the amount is
  // what the warehouse would key). False = live-rated or no-rate-tool
  // carrier, where a keyed amount is an OVERRIDE of the carrier quote.
  truckload_carrier?: boolean;
  /**
   * Latest push of the Charge Freight waiver to the sales site
   * (freight_waiver_pushes): whether the invoice build will honour the
   * waiver. Null when never attempted (bills as normal / push disabled).
   */
  freight_waiver_push?: FreightWaiverPushInfo | null;
  // Open freight ask from the portal-triggered BOL scan: the ship is held
  // until the CSR keys the amount here. Null when nothing is waiting.
  freight_needs_input?: FreightNeedsInputInfo | null;
  /** Open PRO / ship-confirmation ask on the order's scanned BOL (kanban cee85483). */
  bol_scan_desk_ask?: BolScanDeskAskInfo | null;
  /**
   * Merlin's evidence-backed recommendations on this order (kanban 8d83c042):
   * the Freight row renders the current `freightdis` one as a quiet inline
   * chip. Empty for most orders; decided rows stay (accepted / edited /
   * ignored states).
   */
  recommendations?: OrderRecommendation[];
}

/** Mirrors RecommendationExceptionClass in db/models/order_recommendations.py. */
export type RecommendationExceptionClass = 'freightdis' | 'surchg' | 'price' | 'freight_amt' | 'orderdis';
export type RecommendationStatus = 'proposed' | 'accepted' | 'edited' | 'ignored';
export type RecommendationSourceKind =
  | 'agreement_note'
  | 'agreement_deal'
  | 'quote_deal'
  | 'cs_note'
  | 'production_note'
  | 'sage_customer_note'
  | 'email'
  | 'invoice_history'
  | 'desk_rule';

/** Mirrors OrderRecommendationRead in db/models/order_recommendations.py. */
export interface OrderRecommendation {
  id: string;
  order_id: string;
  exception_class: RecommendationExceptionClass;
  field: string;
  /** Chip text, e.g. "Recommended -$400.00 freight discount". */
  label: string;
  /** Positive dollars (a discount of 400 is "400.00"). */
  recommended_value: string;
  applied_value: string | null;
  /** The evidence, verbatim. */
  quote: string;
  source_kind: RecommendationSourceKind;
  source_label: string;
  source_ref: string | null;
  source_date: string | null;
  source_author: string | null;
  reason: string | null;
  effect_text: string;
  status: RecommendationStatus;
  decided_at: string | null;
  decided_by_name: string | null;
  created_at: string;
}

export type RecommendationDecisionAction = 'accept' | 'edit' | 'ignore' | 'reopen';

export interface RecommendationDecisionRequest {
  action: RecommendationDecisionAction;
  /** Required for edit: positive dollars. */
  applied_value?: string | null;
  note?: string | null;
}

export interface RecommendationApplyResult {
  edit_path: 'draft' | 'sage';
  amount: string;
  line_item_id: string | null;
  sage_push: {
    success: boolean;
    sage_order_no: string | null;
    sage_write_id: string | null;
    error_message: string | null;
    http_status: number | null;
  } | null;
}

export interface RecommendationDecisionResponse {
  recommendation: OrderRecommendation;
  apply_result: RecommendationApplyResult | null;
}

/** Mirrors TruckloadFreightSummary in db/models/order_freight_presets.py.
 *  Decimals serialize as strings — coerce with Number() for display. */
/** Latest sales-site push of the current amount (freight_cost_pushes). */
export interface TruckloadFreightPushInfo {
  status: 'applied' | 'applied_test' | 'rejected' | 'error' | string;
  message: string;
  http_status: number | null;
  attempted_at: string;
}

export interface TruckloadFreightSummary {
  preset_id: string;
  freight: string | number;
  fuel: string | number;
  total: string | number;
  note: string | null;
  set_by_user_id: string | null;
  set_by: string | null;
  set_at: string;
  // Number of versions ever set on the order (1 = never edited).
  history_count: number;
  // Newest push of this version to the sales site; null when never pushed.
  last_push?: TruckloadFreightPushInfo | null;
}

/** One row of the append-only truckload freight history (newest first). */
export interface TruckloadFreightHistoryEntry {
  preset_id: string;
  freight: string | number;
  fuel: string | number;
  total: string | number;
  note: string | null;
  source: 'portal_manual';
  set_by_user_id: string | null;
  set_by: string | null;
  set_at: string;
  superseded_at: string | null;
}

/**
 * Outcome of the best-effort push of the Charge Freight waiver to the sales
 * site (freight_waiver_pushes) — the machine-readable signal the invoice
 * build honours; the /C line is only the human-readable record.
 */
export interface FreightWaiverPushInfo {
  status: 'applied' | 'applied_test' | 'rejected' | 'error';
  sage_order_no: string;
  waived: boolean;
  http_status: number | null;
  message: string;
}

/** Outcome of the best-effort push to the sales site (freight_cost_pushes). */
export interface FreightCostPushInfo {
  status: 'applied' | 'applied_test' | 'rejected' | 'error';
  sage_order_no: string;
  freight_amount: string | number;
  fuel_surcharge_amount: string | number;
  http_status: number | null;
  message: string;
}

export interface TruckloadFreightUpdateRequest {
  freight_amount: string;
  // Omit/null → the backend defaults to 10% of freight.
  fuel_surcharge_amount?: string | null;
  note?: string | null;
}

export interface TruckloadFreightUpdateResponse {
  succeeded: boolean;
  outcome: 'set' | 'updated' | 'no_change' | 'not_truckload_carrier' | 'order_invoiced';
  message: string;
  truckload_freight: TruckloadFreightSummary | null;
  push: FreightCostPushInfo | null;
  // The automatic bolScanProcess re-call when a BOL scan was waiting for this
  // freight; null when nothing was waiting.
  scan_resubmit?: BolScanResubmitInfo | null;
}

// ---- Merlin order-edit proposals (order_edit_proposals rows) ----
/**
 * A difference Merlin SAW on the revised PO but chose NOT to propose (kanban
 * ced71e0f — AU02071's renamed/re-priced line 7). Display-only: never applied,
 * never counted as a change; shown so the CSR gives it a look before the
 * order ships. Mirrors services/orders/proposal_unproposed_deltas.py.
 */
export interface UnproposedDeltaPayload {
  line_item_id?: string | null;
  item_code?: string | null;
  aspect: 'quantity' | 'unit_price' | 'description' | 'product' | 'other' | string;
  order_value?: string | null;
  po_value: string;
  reason?: string | null;
}

/** Mirrors OrderEditProposalRead in db/models/orders.py. */
export interface OrderEditProposalRead {
  id: string;
  order_id: string;
  source_email_id?: string | null;
  proposal_key: string;
  status: 'proposed' | 'confirmed' | 'dismissed' | 'superseded' | 'expired';
  summary: string;
  source_request_text?: string | null;
  payload: {
    header?: Record<string, unknown> | null;
    line_updates?: SageLineUpdatePayload[];
    line_adds?: SageLineAddPayload[];
    line_removes?: string[];
    // The old→new diff, computed server-side from DB truth at proposal time.
    changes?: SageFieldChangePayload[];
    /** Unmarked PO differences Merlin left alone; absent on legacy rows. */
    unproposed_deltas?: UnproposedDeltaPayload[];
    /** Merlin's open questions for the CSR (may carry **bold**); absent on legacy rows. */
    open_questions?: string[];
  };
  conversation_id?: string | null;
  responded_by?: string | null;
  responded_at?: string | null;
  sage_write_id?: string | null;
  /** The CSR-adjusted payload actually applied (null = confirmed verbatim). */
  confirmed_payload?: OrderEditProposalRead['payload'] | null;
  /**
   * Set on ``expired`` rows only — why the watchdog settled it without a CSR:
   * 'order_closed' (order shipped/invoiced/cancelled before anyone acted) or
   * 'already_applied' (the change was keyed by hand).
   */
  expired_reason?: 'order_closed' | 'already_applied' | 'answered_by_desk' | string | null;
  /** The CSR's reason on a dismissed proposal (required on dismiss). */
  dismiss_reason?: string | null;
  created_at: string;
}


/** Mirrors OrderEditProposalEdits in db/models/orders.py — the CSR-adjusted
 *  FULL set sent with confirm; re-validated server-side against DB truth. */
export interface OrderEditProposalEditsPayload {
  header?: Record<string, string | null> | null;
  line_updates?: Array<{
    line_item_id: string;
    quantity?: number;
    unit_price?: number;
  }>;
  line_adds?: Array<{
    item_code: string;
    item_description: string;
    quantity: number;
    unit_price?: number;
    no_price_confirmed?: boolean;
  }>;
  line_removes?: string[];
}

// ---- Edit-in-Sage payloads (POST /portal/orders/{id}/sage-edit) ----
export interface SageLineUpdatePayload {
  line_item_id: string;
  item_code?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  /**
   * Text to ADD to the Sage line's CommentText. Append-only at the far end:
   * Adam's BOI writes `existing | new`, ignores text already contained in the
   * current comment, and treats blank as "leave it alone". There is no
   * replace and no clear (kanban 099373a1, sandbox-verified 2026-09-09).
   */
  sage_comment_text?: string | null;
}
export interface SageLineAddPayload {
  item_code: string;
  item_description: string;
  quantity?: number | null;
  unit_price?: number | null;
  /** The operator confirmed sending this add without a price; the server
   *  applies it as an explicit $0.00 (kanban dc8caa8d / review y4yvpz). */
  no_price_confirmed?: boolean;
  /**
   * Where a Merlin proposal's add price came from (server-settled at
   * proposal time): 'stated' | 'no_charge' | a waterfall step
   * ('special_pricing', 'customer_quote', ...) | 'unresolved'. Read-only.
   */
  price_source?: string | null;
  /**
   * True when no contract/quote/catalog price could be resolved for the
   * add — the CSR must key a price before the proposal can be confirmed
   * (an unpriced line lands in Sage at $0.00; kanban dc8caa8d).
   */
  price_required?: boolean | null;
}
export interface SageFieldChangePayload {
  field: string;
  old?: unknown;
  new?: unknown;
  kind?: string;
}
export interface SageOrderEditPayload {
  header?: Record<string, unknown> | null;
  line_updates?: SageLineUpdatePayload[];
  line_adds?: SageLineAddPayload[];
  line_removes?: string[];
  changes?: SageFieldChangePayload[];
  /**
   * "Push anyway" for soft server advisories (a two-part kit's COMPONENT
   * added without its kit parent — AU02050). Without it the server holds
   * the edit and answers requires_acknowledgment; nothing is applied.
   */
  acknowledge_warnings?: boolean;
  /**
   * "Yes, re-stage it": the order is already STAGED on the dock and this edit
   * changes the shipment. Without it the server holds the edit
   * (requires_acknowledgment + staging); with it the push goes through, is
   * audited as an override and emails shipping the change (kanban 42a2ed0a).
   */
  staged_override?: boolean;
  /**
   * Who the override notice goes to: the "Send email to" box in the prompt,
   * prefilled with the suggested recipients and editable by the CSR. Omitted
   * or empty = the server's suggested list.
   */
  staged_notice_recipients?: string[];
}

/** One address on the staged-order notice list (mirrors StagedNoticeRecipientRead). */
export interface StagedNoticeRecipient {
  id: string;
  email: string;
  name?: string | null;
  is_active: boolean;
  created_at: string;
  created_by_user_id?: string | null;
  deactivated_at?: string | null;
  deactivated_by_user_id?: string | null;
  deactivation_reason?: 'removed' | 'edited' | null;
  replaces_id?: string | null;
}

/** Mirrors OrderStagingRead / OrderStagingStatus (GET /portal/orders/{id}/staging). */
export interface OrderStagingStatus {
  sales_order_no: string;
  /** The stager ticked Staged on the sales site (ready_for_shipping = 1). */
  staged: boolean;
  staged_at?: string | null;
  est_ship_date?: string | null;
  ship_via?: string | null;
  sales_site_status?: string | null;
  /** The sales site could not be read (or the order is not on it). */
  unknown?: boolean;
  unknown_reason?: string | null;
  /** Who an override would email (settings), for the modal's wording. */
  notice_recipients?: string[];
}
// ---- New-ship-to create (POST /portal/orders/{id}/sage-ship-to) ----
export interface SageShipToCreatePayload {
  /** Omit to auto-allocate the next free code server-side (the safe default). */
  ship_to_code?: string | null;
  ship_to_name?: string | null;
  ship_to_address1?: string | null;
  ship_to_address2?: string | null;
  ship_to_address3?: string | null;
  ship_to_city?: string | null;
  ship_to_state?: string | null;
  ship_to_zip_code?: string | null;
  ship_to_country_code?: string | null;
  /**
   * The rep to link on the new ship-to (SO_ShipToAddress.SalespersonNo).
   * Omit/null = the server derives the customer's own Sage rep (fail-open).
   * Must be a known Sage salesperson code (validated server-side).
   */
  salesperson_no?: string | null;
  /**
   * Confirm ("create anyway") address-validation warnings from a prior
   * attempt that returned requires_acknowledgment. The override is recorded
   * on the sage_writes audit row. Hard errors are never overridable.
   */
  acknowledge_address_warnings?: boolean;
  /**
   * Confirm a street/city/state/zip disagreement with Merlin's
   * proposed_new_ship_to from a prior attempt (requires_acknowledgment with
   * proposal_diff_holds). Recorded on the sage_writes audit row.
   */
  acknowledge_proposal_diff?: boolean;
}
/** One field where the entered ship-to differs from Merlin's PO reading. */
export interface ShipToProposalFieldDiff {
  field: 'name' | 'address1' | 'city' | 'state' | 'zip';
  label: string;
  proposed: string;
  entered: string;
  /** True for the fields that route the truck (never for name). */
  holds: boolean;
}
export interface SageShipToCreateResponse {
  success: boolean;
  ship_to_code?: string;
  customer_no?: string;
  allocated?: boolean;
  tax_schedule?: string | null;
  tax_schedule_source?: string | null;
  tax_schedule_warning?: string | null;
  /** The rep actually linked (CSR-chosen or derived); null = rep-less. */
  salesperson_no?: string | null;
  /** Held: overridable address warnings need an explicit "create anyway". */
  requires_acknowledgment?: boolean;
  address_warnings?: string[];
  /** How the entered record compares with Merlin's proposed_new_ship_to. */
  proposal_diff?: ShipToProposalFieldDiff[];
  proposal_diff_holds?: boolean;
  /** True when an existing portal-created code was edited in place. */
  edited?: boolean;
  error_message?: string;
  http_status?: number;
  latency_ms?: number;
}
/**
 * The ship-to the portal most recently created/edited FOR an order
 * (GET /portal/orders/{id}/sage-ship-to/created) — what the form offers to
 * edit instead of allocating a second code (kanban 6a7b740b).
 */
export interface PortalOrderCreatedShipTo {
  sage_write_id: string;
  kind: string;
  status: string;
  ship_to_code: string;
  customer_no: string;
  ship_to_name?: string | null;
  ship_to_address1?: string | null;
  ship_to_address2?: string | null;
  ship_to_address3?: string | null;
  ship_to_city?: string | null;
  ship_to_state?: string | null;
  ship_to_zip_code?: string | null;
  ship_to_country_code?: string | null;
  salesperson_no?: string | null;
  created_at: string;
  created_by_email?: string | null;
  is_order_ship_to: boolean;
}
// ---- Customer Sage rep (GET /portal/customers/{no}/sage-rep) ----
export interface CustomerSageRep {
  customer_no: string;
  salesperson_no: string | null;
  salesperson_name: string | null;
}
// ---- Sage write history (GET /portal/orders/{id}/sage-writes) ----
export interface SageWriteAuditEntry {
  id: string;
  kind: string;
  status: string;
  source: string;
  sage_order_no?: string | null;
  entity_ref?: string | null;
  changes?: SageFieldChangePayload[] | null;
  error_message?: string | null;
  http_status?: number | null;
  latency_ms?: number | null;
  created_by_email?: string | null;
  created_at: string;
  submitted_at?: string | null;
  confirmed_at?: string | null;
  failed_at?: string | null;
}

export interface SiblingOrderSummary {
  id: string;
  po_number: string | null;
  status: string;
  customer_name: string | null;
  line_item_count: number;
  /** Which email in the thread this order was created from. */
  source_email_id?: string | null;
}

/**
 * One existing order matching a (customer_no, po_number) pair on the
 * duplicate-PO check. Mirrors `DuplicatePoOrderSummary` in
 * `app/routes/portal_orders.py`.
 */
export type DuplicatePoMatchKind = 'open' | 'invoiced';

export interface DuplicatePoOrderSummary {
  /**
   * Portal order id. Null for a Sage-invoice-only match: the PO was keyed
   * straight into Sage, so there is no portal order to open.
   */
  id: string | null;
  sage_order_no: string | null;
  po_number: string | null;
  /** OrderStatus value, or `invoiced` for a Sage-invoice-only match. */
  status: string;
  customer_name: string | null;
  line_item_count: number;
  created_at: string | null;
  /**
   * `open` = a live portal order (adjust it instead of creating a new
   * one); `invoiced` = the PO already shipped and was billed, through a
   * completed portal order or Sage invoice history (kanban f1f3600c).
   */
  match_kind: DuplicatePoMatchKind;
  invoice_no: string | null;
  /** ISO date (YYYY-MM-DD) of the newest invoice for this sales order. */
  invoice_date: string | null;
}

export interface DuplicatePoCheckResponse {
  duplicates: DuplicatePoOrderSummary[];
}

export interface EmailOrdersResponse {
  email_id: string;
  orders: SiblingOrderSummary[];
  total: number;
}

export interface PortalOrderStatsResponse {
  total_orders: number;
  draft_count: number;
  submission_failed_count: number;
  submitted_count: number;
  confirmed_count: number;
  accepted_count: number;
  shipped_count: number;
  completed_count: number;
  cancelled_count: number;
  /**
   * Total orders with a non-NULL ``cancel_reason_code``. Sum of
   * ``on_hold_by_code`` values; pre-computed server-side so the page can
   * render the "On hold" tab badge with a single fetch.
   */
  on_hold_count: number;
  /**
   * Un-answered Merlin edit proposals (customer-requested changes waiting on a
   * CSR) and how many have waited past the aging threshold. Both 0 while the
   * Edit-in-Sage feature is off.
   */
  pending_edit_proposal_count?: number;
  aging_edit_proposal_count?: number;
  /**
   * Per-code breakdown of held orders, e.g. {"FRT": 5, "HOLD": 2}. Codes
   * with zero orders are omitted. Joined with the synced
   * ``sage_cancel_reason_codes`` lookup on the FE to display descriptions.
   */
  on_hold_by_code: Record<string, number>;
}

export interface PortalTransitionRequest {
  action: string;
  notes?: string;
  reason?: string;
  // For action="submit": acknowledge soft ship-to "doesn't match" warnings and
  // submit anyway (set by the "Submit anyway" confirm).
  acknowledge_warnings?: boolean;
  // For action="confirm_sage": the Sage order number (AU#) the order was keyed
  // under. Required unless the order is already open in Sage; the server
  // refuses a number that is not an open Sage SO for the same customer.
  sage_order_no?: string;
}

export interface WarehouseOption {
  warehouse_code: string;
  item_count: number;
}

/** GET /portal/orders/{id}/header-identity-check */
export interface HeaderIdentityCheckResponse {
  warn: boolean;
  field: 'customer_no' | 'po_number' | null;
  origin_customer_no: string | null;
  origin_po_number: string | null;
  proposed_value: string | null;
  message: string | null;
  already_detached: boolean;
}

/** POST /portal/orders/{id}/detach-source-identity */
export interface DetachSourceIdentityRequest {
  reason: 'different_order';
  note?: string | null;
}

export interface PortalOrderUpdateRequest {
  customer_no?: string;
  customer_name?: string;
  salesperson_no?: string;
  salesperson_name?: string;
  payment_terms?: string;
  ship_to_address?: Record<string, unknown>;
  freight_cost?: string;
  po_number?: string;
  requested_ship_date?: string;
  requested_ship_date_confirmed?: boolean;
  udf_jobsite?: string;
  order_type?: 'S' | 'B';
  warehouse_code?: string;
  ship_via?: string;
  udf_bl?: string;
  /** UDF_QUOTE slots: a string attaches a quote ref, null clears the slot. */
  udf_quote?: string | null;
  udf_quote_2?: string | null;
  udf_quote_3?: string | null;
  udf_quote_4?: string | null;
  udf_quote_5?: string | null;
  cancel_reason_code?: string | null;
}

export interface BackorderPrompt {
  id: string;
  order_id: string;
  line_item_id: string;
  item_code: string;
  qty_before: string;
  qty_after: string;
  remaining_qty: string;
  accepted_qty: string | null;
  status: 'offered' | 'accepted' | 'declined' | 'superseded';
  created_backorder_order_id: string | null;
  created_at: string;
  responded_at: string | null;
}

export interface BackorderPromptAcceptResponse {
  prompt: BackorderPrompt;
  backorder_order: OrderRecord;
}

export interface CancelReasonCodeOption {
  cancel_reason_code: string;
  description: string | null;
  /** Sage has the code flagged Inactive — Sage refuses it on an order. Never offer as a choice. */
  is_inactive?: boolean;
}

/** Portal-vs-Sage hold state for an order (GET /portal/orders/{id}/hold). */
export type HoldSyncState =
  | 'no_hold'
  | 'set_pending'
  | 'confirmed'
  | 'held_unlabeled'
  | 'release_pending'
  | 'released';

export interface OrderHoldStatus {
  /** False for drafts — the hold rides the submission payload. */
  in_sage: boolean;
  state: HoldSyncState;
  portal_code: string | null;
  portal_description: string | null;
  /** What the Sage SO mirror currently reports. */
  sage_code: string | null;
  sage_order_status: string | null;
  sage_synced_at: string | null;
  set_by: string | null;
  set_at: string | null;
  confirmed_at: string | null;
  release_requested_by: string | null;
  release_requested_at: string | null;
  /** A pending set/release has outlived the expected drain + re-check window. */
  overdue: boolean;
}

export interface OrderHoldChangeResponse {
  succeeded: boolean;
  outcome: string;
  message: string;
  order: OrderRecord;
  hold: OrderHoldStatus;
  sage_write_id: string | null;
}

/** Result of POST /portal/orders/{id}/park and /unpark. */
export interface OrderParkChangeResponse {
  order: OrderRecord;
  parked: boolean;
  alerts_answered: number;
  message: string;
}

export interface ShippingCarrierOption {
  shipping_code: string;
  description: string | null;
}

export interface FreightQuoteItem {
  /** Freight-API identifier (e.g. "rl", "pitt") — used only for display/tracking. */
  carrier: string;
  /**
   * Canonical Sage ShippingCode (e.g. "R&L", "PITT") — the value to write
   * to order.ship_via on selection. Never uppercase or munge `carrier`
   * client-side; always use this resolved code.
   */
  ship_via_code: string;
  rate: number | null;
  error: string | null;
  is_recommended: boolean;
  freight_charge: number | null;
  fuel_surcharge: number | null;
  transit_days: number | null;
  delivery_date: string | null;
}

/**
 * The resolved `request` block that Sage's Freight API echoes back.
 *
 * Surfaced to ALL portal users — the user-facing fields (address,
 * weights, skids, hazmat/freezable flags) let the order detail UI show
 * the skid-inclusive weight Sage actually used to compute freight.
 * `attempts` and `http_status` are admin-only debug fields; non-admins
 * receive them as `null`.
 */
export interface FreightRequestSummary {
  shipping_city: string | null;
  shipping_state: string | null;
  shipping_zip: string | null;
  product_weight: number | null;
  skid_weight: number | null;
  total_skids: number | null;
  total_weight: number | null;
  has_freezable_items: boolean | null;
  has_hazmat_items: boolean | null;
  // Admin debug — non-admins receive these as null even when the
  // summary itself is populated.
  attempts: number | null;
  http_status: number | null;
}

/**
 * Visible to all users. Data-quality warnings or errors detected on the
 * freight quote response that the salesperson should see before picking
 * a carrier (e.g. weight looks truncated, 0 skids, hazmat catalog
 * mismatch).
 */
export interface FreightDataIssue {
  code:
    | 'weight_truncated'
    | 'no_skids'
    | 'weight_too_low'
    | 'hazmat_catalog_mismatch'
    | 'hazmat_code_without_bl_description'
    | 'carrier_partial'
    | 'carrier_none';
  severity: 'warning' | 'error';
  message: string;
}

export interface FreightQuotesResponse {
  success: boolean;
  quotes: FreightQuoteItem[];
  error_message: string | null;
  recommended_carrier: string | null;
  latency_ms: number;
  issues: FreightDataIssue[];
  request_summary: FreightRequestSummary | null;
}

/**
 * Response from GET /portal/orders/{orderId}/weight-summary.
 *
 * Asks Sage's freight API (with_quotes=false mode — ~60-70× faster than a
 * full quote fetch) for just the weight envelope so the line-items page can
 * show the same total the freight quote modal does, including skid weight
 * the local sum doesn't see.
 *
 * On any failure (no ship-to address, no zip, freight API error) the
 * backend returns `success: false` with a structured `fallback_reason`,
 * and the caller renders its locally-computed sum instead.
 */
export interface WeightSummaryResponse {
  success: boolean;
  fallback_reason:
    | 'no_ship_to_address'
    | 'no_zip_code'
    | 'no_line_items'
    | 'zip_not_found'
    | 'freight_api_error'
    | null;
  error_message: string | null;
  latency_ms: number;
  product_weight: number | null;
  skid_weight: number | null;
  total_weight: number | null;
  total_skids: number | null;
  has_freezable_items: boolean | null;
  has_hazmat_items: boolean | null;
}

export interface LineItemUpdateRequest {
  item_code?: string;
  item_description?: string;
  product_line?: string;
  quantity?: string;
  unit_price?: string;
  discount_percent?: string;
  packaging?: string;
  notes?: string;
  /**
   * Sage-bound per-line comment: written to the Sage detail line's native
   * CommentText on submission. Distinct from `notes` (internal-only).
   * Empty string clears it (no comment sent).
   */
  sage_comment_text?: string;
  /** Clear (false) or re-raise (true) the per-line review flag. */
  needs_review?: boolean;
  /** How the CSR answered the review card; null to undo a resolution. */
  review_resolution?: ReviewResolution | null;
  /**
   * Optional note on why the price differs from the customer's PO price
   * (owner ask 2026-10-29). Audited as its own row; may travel with
   * `unit_price` or alone right after the price saved. Max 500 chars.
   */
  price_change_reason?: string;
}

export interface LineItemMutationResponse {
  line_item: OrderLineItem;
  order_subtotal: string;
  order_total: string | null;
  // Soft, line-scoped advisories about the line just touched (never-bought
  // SKU while the account buys a variant sibling). Same shape as the submit
  // gate's submission_warnings; null/absent when nothing qualifies.
  advisories?: SubmissionFieldError[] | null;
}

export interface LineItemDeleteResponse {
  order_subtotal: string;
  order_total: string | null;
}

export interface BulkDismissSkipped {
  order_id: string;
  /** `blocked`: a linked record (plain FK) stopped the delete; `detail` names it. */
  reason: 'not_found' | 'not_draft' | 'blocked' | 'error';
  detail: string | null;
}

export interface BulkDismissDraftsResponse {
  dismissed: number;
  skipped: number;
  skipped_details: BulkDismissSkipped[];
}

// Pricing Context types for order validation

// Matches backend PricingSourceType. 'base_discount' is the customer's
// agreement-level discount applied to the catalog list (the resolver's
// `special_pricing_base_discount`) — an on-contract price, not 'custom'.
export type PricingSourceType =
  | 'special'
  | 'tiered'
  | 'quote'
  | 'base_discount'
  | 'standard'
  | 'custom'
  | 'none';

export type ListedPricingSourceType =
  | 'special'
  | 'tiered'
  | 'quote'
  | 'base_discount'
  | 'standard';

/** Which known source the current unit_price matches (backend-classified). */
export interface DetectedPricingSource {
  type: PricingSourceType;
  quote_source_id: number | null;
  tier_label: string | null;
  tier_price: number | null;
  /** `base_discount` only: % off the catalog list, and that list price. */
  base_discount_pct?: number | null;
  list_price?: number | null;
}

/** One pricing source available for a line item (backend-built). */
export interface PricingSourceOption {
  type: ListedPricingSourceType;
  label: string;
  sublabel: string | null;
  price: number;
  quote_source_id: number | null;
  /** True when this option equals the current ordered unit_price. */
  is_active: boolean;
}

export interface LineItemPricingContext {
  item_code: string;
  item_description: string | null;
  quantity: string;
  unit_price: string | null;
  standard_price: string | null;
  ship_weight: string | null;
  /**
   * User-defined ship weight (Sage CI_Item.UDF_SHIP_WGT_LBS). Preferred
   * over {@link ship_weight} because Sage's freight API uses it. May be
   * null for older products; fall back to {@link ship_weight} then.
   */
  udf_ship_wgt_lbs: string | null;
  special_price: number | null;
  special_price_note: string | null;
  has_special_pricing: boolean;
  discount_pct: number | null;
  pricing_type: string | null;
  tier_price_1: number | null;
  tier_price_2: number | null;
  tier_price_3: number | null;
  tier_price_2_min: number | null;
  tier_price_3_min: number | null;
  // Price resolution waterfall result
  resolved_price: string | null;
  price_source: string | null; // "product_hybrid" | "special_pricing" | "special_pricing_base_discount" | "customer_quote" | "investigation"
  price_source_detail: string | null;
  // Populated when price_source === "special_pricing_base_discount":
  // % taken off catalog list and the pre-discount list price. The line
  // badge renders both so the discount is visible at a glance.
  applied_base_discount_pct: number | null;
  list_price_before_discount: string | null;
  // Backend-classified source of the current unit_price + all options.
  current_source: DetectedPricingSource | null;
  available_sources: PricingSourceOption[];
  // LLM-extracted PO price (from email-detected orders only). When this
  // disagrees with the current `unit_price` the line-item card shows
  // a yellow "PO says $X" warning badge.
  extracted_price: string | null;
  has_po_price_mismatch: boolean;
  /**
   * Active-make sheet status ('active' | 'MTO' | 'inactive' | ...).
   * Null when the item is not on the sheet or sheet data is unavailable.
   */
  sheet_status: string | null;
  /**
   * True when the item is sheet-inactive OR missing from a populated
   * active-make sheet — render the amber "confirm this is correct" warning.
   */
  active_list_warning: boolean;
  /**
   * Expired-quote flow (2026-08-26): set when this line's price matches an
   * EXPIRED project quote that live pricing can't explain. Renders the
   * red "EXPIRED QUOTE #n" pill beside the Source badge. Null when the
   * feature flag is off or no match exists.
   */
  expired_quote_match?: LineExpiredQuoteMatch | null;
}

export interface OrderExpiredQuoteAlertStatus {
  alert_id: string;
  quote_source_id: number;
  // pending_send | awaiting_response | approved | denied | disputed | cancelled | send_failed
  status: string;
  salesperson_no: string | null;
  salesperson_name: string | null;
  first_sent_at: string | null;
  last_sent_at: string | null;
  send_count: number;
  decided_at: string | null;
  approval_source: string | null;
  /** status === 'disputed': why the salesperson says the alert is wrong, in their words. */
  dispute_reason?: string | null;
  /** decided_at − first_sent_at, seconds — the response-speed audit. */
  response_seconds: number | null;
  /** The Merlin conversation the alert is held as (agent-console deep link). */
  conversation_id?: string | null;
  /** Messages Merlin sent (alert, reminders, answers) / replies the salesperson sent. */
  merlin_messages?: number;
  salesperson_replies?: number;
  /** Most recent crossing in either direction. */
  last_message_at?: string | null;
  /** The banner's Nudge button: offered or not, and why not
   *  (not_awaiting_response | nudge_pending | too_soon | limit_reached). */
  can_nudge?: boolean;
  nudge_blocked?: string | null;
  /** A nudge was asked for and Merlin has not sent it yet. */
  nudge_pending?: boolean;
  nudge_count?: number;
  last_nudge_by?: string | null;
  /** When the last nudge was asked for / when it reached the salesperson. */
  last_nudge_at?: string | null;
  last_nudge_sent_at?: string | null;
  last_nudge_failed?: boolean;
  /** status === 'denied': where the Sage price re-check stands
   *  (watching | re_alerted | resolved | invoiced_unchanged | order_gone | unverified). */
  denial_followthrough?: string | null;
  denial_checked_at?: string | null;
  denial_invoice_no?: string | null;
  denial_lines?: DeniedLineStatus[];
  /** approval_source === 'quote_renewed': the salesperson renewed the quote on
   *  the sales site instead of replying; when, and the renewed expiration. */
  renewed_on?: string | null;
  renewed_valid_through?: string | null;
}

/** One denied line as Sage carried it at the last re-check transition. */
export interface DeniedLineStatus {
  item_code: string;
  denied_price: number | string;
  /** Every unit price Sage carried for the item (empty: no longer on the order). */
  sage_prices: Array<number | string>;
  unchanged: boolean;
}

/** POST …/expired-quote-alerts/{id}/nudge — the nudge was queued for Merlin. */
export interface ExpiredQuoteNudgeResponse {
  alert_id: string;
  nudge_request_id: string;
  requested_by: string;
  requested_at: string;
  salesperson_name: string | null;
}

export interface LineExpiredQuoteMatch {
  quote_source_id: number;
  quoted_price: number;
  quote_created_date: string | null;
  quote_expiration_date: string | null;
  /** True when the quote number is in the order's UDF_QUOTE slots. */
  referenced: boolean;
}

export interface AgreementProduct {
  item_code: string;
  /** Catalog display name (udf_fulldesc > item_code_desc); null when unknown. */
  item_description: string | null;
  special_price: number | null;
  pricing_type: string | null;
  tier_price_1: number | null;
  tier_price_2: number | null;
  tier_price_3: number | null;
  tier_price_2_min: number | null;
  tier_price_3_min: number | null;
  is_active: boolean | null;
  note: string | null;
}

export interface CustomerPricingAgreement {
  id: string;
  effective_date: string | null;
  expiration_date: string | null;
  base_discount: number | null;
  payment_terms: string | null;
  note: string | null;
  is_active: boolean | null;
  /**
   * False when the agreement is still flagged active in the Sales App but
   * today is outside its effective/expiration window (how the Sales App ends
   * a deal). It no longer prices orders and carries no products.
   */
  in_force: boolean;
  has_freight_deal: boolean;
  has_grout_deal: boolean;
  product_count: number;
  products: AgreementProduct[];
  /** Customer number the agreement is filed under in the Sales App. */
  customer_no: string | null;
  /**
   * True when the agreement lives on a linked sibling account rather than the
   * order's own customer number (e.g. TUCKE01 order, deal filed under TUCK01).
   */
  via_linked_customer: boolean;
}

export interface QuoteOverlapItem {
  item_code: string;
  /** Catalog display name (udf_fulldesc > item_code_desc); null when unknown. */
  item_description: string | null;
  quantity: number;
  price: number;
}

export interface CustomerOpenQuote {
  id: string;
  source_id: number;
  project_name: string | null;
  status: string | null;
  created_date: string | null;
  expiration_date: string | null;
  product_count: number;
  estimated_total: number | null;
  overlapping_items: QuoteOverlapItem[];
}

export interface QuoteProductDetail {
  item_code: string | null;
  item_description: string | null;
  packaging: string | null;
  quantity: number;
  price: number;
  note: string | null;
}

/** One quote line compared against the order (referenced-quote card). */
export interface ReferencedQuoteLine {
  item_code: string;
  item_description: string | null;
  quote_quantity: number | null;
  quote_price: number | null;
  order_price: number | null;
  on_order: boolean;
  price_matches: boolean;
}

/**
 * A quote referenced on the order via a udf_quote slot, resolved regardless
 * of quote ownership (orphan quotes for new customers included).
 * Mirrors `ReferencedOrderQuote` in `app/schemas/pricing_context.py`.
 */
export interface ReferencedOrderQuote {
  slot: string;
  raw_reference: string;
  found: boolean;
  source_id: number | null;
  pc_name: string | null;
  pc_contact_name: string | null;
  project_name: string | null;
  status: string | null;
  created_date: string | null;
  expiration_date: string | null;
  is_expired: boolean;
  customer_linked: boolean;
  belongs_to_order_customer: boolean;
  merlin_verified: boolean | null;
  merlin_note: string | null;
  lines: ReferencedQuoteLine[];
}

export interface QuoteDetailResponse {
  id: string;
  source_id: number;
  project_name: string | null;
  status: string | null;
  created_date: string | null;
  expiration_date: string | null;
  freight_type: string | null;
  freight_estimate: number | null;
  has_freight_deal: boolean;
  has_grout_deal: boolean;
  payment_terms: string | null;
  product_count: number;
  estimated_total: number | null;
  products: QuoteProductDetail[];
}

/**
 * Lightweight quote summary returned by the FE linked-quote picker.
 * Mirrors `PortalQuoteSummary` in `app/routes/portal_orders.py`.
 */
export interface PortalQuoteSummary {
  id: string;
  source_id: number;
  project_name: string | null;
  status: string;
  created_date: string;
  expiration_date: string | null;
  product_count: number;
  estimated_total: number | null;
}

export interface OrderPricingContextResponse {
  customer_no: string;
  customer_name: string | null;
  /**
   * Per-ship-to pricing override: when set, special pricing and the base
   * discount resolve from THIS customer's price list (e.g. ERSCO02 for
   * WHITE13 orders shipping to Lansing, MI) while quotes/credit-hold stay
   * on customer_no. The pricing tab renders an explanatory banner.
   */
  pricing_customer_no?: string | null;
  pricing_customer_name?: string | null;
  pricing_override_reason?: string | null;
  has_any_special_pricing: boolean;
  line_items: LineItemPricingContext[];
  agreements: CustomerPricingAgreement[];
  open_quotes: CustomerOpenQuote[];
  /** Quotes referenced on the order (udf_quote slots), owner-independent. */
  referenced_quotes?: ReferencedOrderQuote[];
  /** Expired-quote salesperson alerts on this order (2026-08-26 flow). */
  expired_quote_alerts?: OrderExpiredQuoteAlertStatus[];
  items_with_special_pricing: number;
  items_without_special_pricing: number;
  /** True when the customer's Sage AR_Customer.CreditHold = 'Y'. */
  customer_on_hold: boolean;
  /** Raw Sage hold flag ('Y' / 'N' / null) for audit. */
  customer_hold_code: string | null;
}

/**
 * Both Sage note fields for an order (from chemmas_sales).
 * `order_notes` is production / "Kevin" notes — always read-only.
 * `order_cs_notes` is customer-service notes — the only writable field.
 */
export interface SageCsNoteRow {
  id: string | null;
  date_label: string | null;
  text: string;
  formatted_entry: string | null;
  source: string;
  status: string;
  can_archive: boolean;
  created_at: string | null;
  synced_at: string | null;
  error: string | null;
}

export interface SageOrderNotes {
  sales_order_no: string | null;
  order_notes: string | null;
  order_cs_notes: string | null;
  order_cs_note_rows: SageCsNoteRow[];
  order_notes_read_only: boolean;
  order_cs_notes_writable: boolean;
  available: boolean;
  unavailable_reason: string | null;
}

/** Result of a CS-notes queue or archive action. */
export interface CsNotesUpdateResponse {
  succeeded: boolean;
  outcome: string;
  message: string;
  notes: SageOrderNotes;
  entry_id: string | null;
}

/** A `/`-prefixed Sage memo row that annotates a parent product line. */
export interface SageLineMemo {
  code: string;
  text: string;
}

/** Per-product-line Sage annotation bundle. */
export interface LineCommentBundle {
  item_code: string | null;
  position: number;
  sage_comment_text: string | null;
  memo_comments: SageLineMemo[];
}

/** Response envelope for GET /portal/orders/{id}/line-comments. */
export interface OrderLineComments {
  available: boolean;
  source: 'sage_open' | 'sage_invoiced' | null;
  unavailable_reason: string | null;
  bundles: LineCommentBundle[];
}

/** One order-level instruction destined for a standalone Sage /C row. */
export type InstructionRemovalContext = 'csr_dismissed' | 'left_off_at_submit';

export interface OrderInstruction {
  id: string;
  order_id: string;
  position: number;
  text: string;
  source: 'merlin' | 'csr';
  sage_state:
    | 'suggested'
    | 'pending'
    | 'submitted'
    | 'confirmed'
    | 'failed'
    | 'removed'
    | 'desk_removed';
  created_by: string | null;
  accepted_by: string | null;
  /** How a `removed` row was removed: dismissed by hand in the section, or
   *  stamped by Submit to Sage with no prompt (kanban 88963483). Null
   *  otherwise, and on rows removed before the column existed. */
  removal_context: InstructionRemovalContext | null;
  sage_write_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Outcome of a live-Sage instruction push (narrow /C-only write). */
export interface InstructionPushResult {
  outcome:
    | 'submitted'
    | 'no_pending_instructions'
    | 'order_not_in_sage'
    | 'live_order_unavailable'
    | 'duplicate_item_codes'
    | 'submit_failed';
  message: string;
  sage_write_id: string | null;
  instruction_ids: string[];
}

/** Response envelope for GET /portal/orders/{id}/instructions. */
export interface InstructionListResponse {
  instructions: OrderInstruction[];
}

/** Result of an instruction mutation plus the refreshed list. */
export interface InstructionMutationResponse {
  succeeded: boolean;
  message: string;
  instructions: OrderInstruction[];
  push: InstructionPushResult | null;
}

/** Result of a charge-freight flag change (PATCH /{id}/charge-freight). */
export interface ChargeFreightUpdateResponse {
  succeeded: boolean;
  outcome: 'updated' | 'no_change' | 'order_invoiced';
  message: string;
  charge_freight: boolean;
  instructions: OrderInstruction[];
  push: InstructionPushResult | null;
  /** Sales-site waiver push; null when not attempted (draft / push off). */
  waiver_push?: FreightWaiverPushInfo | null;
}

// ---- Order surcharge (/SURCHG line, GET/PUT /portal/orders/{id}/surcharge) ----
/** Mirrors services/orders/surcharge_line.py. Money fields are decimal strings. */
export type SurchargeMode = 'percent' | 'manual' | 'none' | 'default';
export interface SurchargeExcludedLine {
  line_item_id: string;
  item_code: string;
  goods_amount: string;
  reason: 'quote_exempt';
  quote_id: number | null;
}
export interface SurchargeSuggestion {
  percent: string;
  goods_total: string;
  excluded_goods_total: string;
  base_total: string;
  suggested_amount: string;
  excluded_lines: SurchargeExcludedLine[];
}
export interface SurchargeLineState {
  /** 'default' = no /SURCHG line (the invoice sync decides at BOL scan);
   *  'explicit' = the line's amount wins downstream (0.00 = no surcharge). */
  mode: 'default' | 'explicit';
  line_item_id: string | null;
  amount: string | null;
}
export interface SurchargeAccountStatus {
  customer_no: string;
  will_bill: boolean;
  is_exempt: boolean | null;
  observed_at: string | null;
}
export interface SurchargePreview {
  current: SurchargeLineState;
  suggestion: SurchargeSuggestion;
  account: SurchargeAccountStatus | null;
  /** null = not changeable from the portal (invoiced, cancelled, in flight,
   *  or Edit-in-Sage off in this environment). */
  edit_path: 'draft' | 'sage' | null;
}
export interface SurchargeSetRequest {
  mode: SurchargeMode;
  /** Required for 'manual' (>= 0, 2dp), forbidden otherwise. */
  amount?: string | null;
}

export interface SurchargeSagePush {
  success: boolean;
  sage_order_no: string | null;
  error_message: string | null;
  http_status: number | null;
}
export interface SurchargeSetResponse {
  preview: SurchargePreview;
  line_items: OrderLineItem[];
  order_subtotal: string;
  order_total: string | null;
  /** Only on the 'sage' path: how the Edit-in-Sage push went. */
  sage_push: SurchargeSagePush | null;
}

export const portalOrderService = {
  /**
   * Create a draft order from an email that the agent classified as an order
   * but for which no order record was automatically created.
   */
  createOrderFromEmail: async (
    emailId: string,
  ): Promise<PortalOrderDetailResponse> => {
    const response = await api.post<PortalOrderDetailResponse>(
      `/portal/orders/from-email/${emailId}`,
    );
    return response.data;
  },

  /**
   * Create a new manual order (not from email)
   */
  createManualOrder: async (
    data: PortalManualOrderRequest,
  ): Promise<PortalOrderDetailResponse> => {
    const response = await api.post<PortalOrderDetailResponse>(
      '/portal/orders',
      data,
    );
    return response.data;
  },

  /**
   * Get order detail by source email ID.
   * Returns null if no order exists for this email (404).
   */
  getOrderByEmailId: async (
    emailId: string,
  ): Promise<PortalOrderDetailResponse | null> => {
    try {
      const response = await api.get<PortalOrderDetailResponse>(
        `/portal/orders/by-email/${emailId}`,
      );
      return response.data;
    } catch (err: unknown) {
      // 404 means no order for this email - return null
      if (
        err &&
        typeof err === 'object' &&
        'response' in err &&
        (err as { response?: { status?: number } }).response?.status === 404
      ) {
        return null;
      }
      throw err;
    }
  },

  /**
   * Get all orders created from a single email (for multi-order switching).
   */
  getOrdersByEmailId: async (
    emailId: string,
  ): Promise<EmailOrdersResponse> => {
    const response = await api.get<EmailOrdersResponse>(
      `/portal/orders/by-email/${emailId}/all`,
    );
    return response.data;
  },

  /**
   * List orders for portal review
   */
  listOrders: async (params?: {
    status?: string;
    search?: string;
    exclude_terminal?: boolean;
    /**
     * Sage CancelReasonCode filter. Pass a specific code (e.g. "FRT") for
     * exact match, "any" for any non-NULL hold, or "none" for no hold.
     */
    hold_reason?: string;
    sort_by?: string;
    sort_order?: string;
    offset?: number;
    limit?: number;
  }): Promise<PortalOrderListResponse> => {
    const response = await api.get<PortalOrderListResponse>(
      '/portal/orders',
      { params },
    );
    // Guard the list shape at the boundary: PortalOrdersPage feeds `orders`
    // straight into useMemo filters, and a body without it used to surface as
    // `TypeError: t is not iterable` and a blank portal (kanban 90f3b9d8).
    const data = response.data ?? ({} as PortalOrderListResponse);
    const orders = Array.isArray(data.orders) ? data.orders : [];
    return { ...data, orders, total: typeof data.total === 'number' ? data.total : orders.length };
  },

  /**
   * Get order queue statistics
   */
  getStats: async (): Promise<PortalOrderStatsResponse> => {
    const response = await api.get<PortalOrderStatsResponse>(
      '/portal/orders/stats',
    );
    return response.data;
  },

  /**
   * Get full order detail with source email context
   */
  getOrder: async (orderId: string): Promise<PortalOrderDetailResponse> => {
    const response = await api.get<PortalOrderDetailResponse>(
      `/portal/orders/${orderId}`,
    );
    return response.data;
  },

  /**
   * Update a draft order
   */
  updateOrder: async (
    orderId: string,
    data: PortalOrderUpdateRequest,
  ): Promise<OrderRecord> => {
    const response = await api.patch<OrderRecord>(
      `/portal/orders/${orderId}`,
      data,
    );
    return response.data;
  },

  /**
   * Execute a state transition
   */
  transitionOrder: async (
    orderId: string,
    data: PortalTransitionRequest,
  ): Promise<PortalOrderDetailResponse> => {
    const response = await api.post<PortalOrderDetailResponse>(
      `/portal/orders/${orderId}/transition`,
      data,
    );
    return response.data;
  },

  /**
   * Add a line item to a draft order
   */
  addLineItem: async (
    orderId: string,
    data: LineItemCreateRequest,
  ): Promise<LineItemMutationResponse> => {
    const response = await api.post<LineItemMutationResponse>(
      `/portal/orders/${orderId}/line-items`,
      data,
    );
    return response.data;
  },

  /**
   * Update a line item
   */
  updateLineItem: async (
    orderId: string,
    itemId: string,
    data: LineItemUpdateRequest,
  ): Promise<LineItemMutationResponse> => {
    const response = await api.patch<LineItemMutationResponse>(
      `/portal/orders/${orderId}/line-items/${itemId}`,
      data,
    );
    return response.data;
  },

  /**
   * Remove a line item
   */
  removeLineItem: async (
    orderId: string,
    itemId: string,
  ): Promise<LineItemDeleteResponse> => {
    const response = await api.delete<LineItemDeleteResponse>(
      `/portal/orders/${orderId}/line-items/${itemId}`,
    );
    return response.data;
  },

  /**
   * List the still-offered backorder prompts for an order
   */
  listBackorderPrompts: async (orderId: string): Promise<BackorderPrompt[]> => {
    const response = await api.get<{ prompts: BackorderPrompt[] }>(
      `/portal/orders/${orderId}/backorder-prompts`,
    );
    // `{prompts: []}` is the contract; a body without it must read as "no
    // prompts", not crash `backorderPrompts.length` in the modal (kanban 90f3b9d8).
    const prompts = response.data?.prompts;
    return Array.isArray(prompts) ? prompts : [];
  },

  /**
   * Record a quantity-reduction backorder offer (shown inline; no alert)
   */
  createBackorderPrompt: async (
    orderId: string,
    data: { line_item_id: string; qty_before: string; qty_after: string },
  ): Promise<BackorderPrompt> => {
    const response = await api.post<BackorderPrompt>(
      `/portal/orders/${orderId}/backorder-prompts`,
      data,
    );
    return response.data;
  },

  /**
   * Accept a backorder prompt — creates the linked draft (order_type 'B').
   * `quantity` overrides the prompt's computed remainder (editable inline).
   */
  acceptBackorderPrompt: async (
    orderId: string,
    promptId: string,
    quantity?: string,
  ): Promise<BackorderPromptAcceptResponse> => {
    const response = await api.post<BackorderPromptAcceptResponse>(
      `/portal/orders/${orderId}/backorder-prompts/${promptId}/accept`,
      quantity != null ? { quantity } : {},
    );
    return response.data;
  },

  /**
   * Decline a backorder prompt (no backorder needed)
   */
  declineBackorderPrompt: async (
    orderId: string,
    promptId: string,
  ): Promise<BackorderPrompt> => {
    const response = await api.post<BackorderPrompt>(
      `/portal/orders/${orderId}/backorder-prompts/${promptId}/decline`,
    );
    return response.data;
  },

  /**
   * Dismiss (delete) a draft order entirely
   */
  dismissDraft: async (orderId: string): Promise<void> => {
    await api.delete(`/portal/orders/${orderId}`);
  },

  /**
   * Dismiss many draft orders in one request. Non-draft / missing rows
   * are reported in `skipped_details` rather than aborting the batch.
   */
  bulkDismissDrafts: async (
    orderIds: string[],
  ): Promise<BulkDismissDraftsResponse> => {
    const response = await api.post<BulkDismissDraftsResponse>(
      '/portal/orders/bulk-dismiss',
      { order_ids: orderIds },
    );
    return response.data;
  },

  /**
   * Ask Merlin to nudge the salesperson on an unanswered expired-quote alert
   * (the order banner's Nudge button). A refusal is a 409 whose
   * `detail.message` is written for the CSR.
   */
  nudgeExpiredQuoteAlert: async (
    orderId: string,
    alertId: string,
  ): Promise<ExpiredQuoteNudgeResponse> => {
    const response = await api.post<ExpiredQuoteNudgeResponse>(
      `/portal/orders/${orderId}/expired-quote-alerts/${alertId}/nudge`,
    );
    return response.data;
  },

  /**
   * Get pricing context for order validation
   */
  getPricingContext: async (
    orderId: string,
  ): Promise<OrderPricingContextResponse> => {
    const response = await api.get<OrderPricingContextResponse>(
      `/portal/orders/${orderId}/pricing-context`,
    );
    // The modal iterates `line_items` / `open_quotes` unguarded; default the
    // arrays here so a partial body degrades to "no pricing context" instead
    // of a render-time TypeError (kanban 90f3b9d8).
    const data = response.data ?? ({} as OrderPricingContextResponse);
    return {
      ...data,
      line_items: Array.isArray(data.line_items) ? data.line_items : [],
      open_quotes: Array.isArray(data.open_quotes) ? data.open_quotes : [],
    };
  },

  /**
   * List recent sales quotes for a given Sage customer, for the FE
   * linked-quote picker in the create-order dialog.
   */
  listQuotesForCustomer: async (
    customerNo: string,
    limit = 50,
  ): Promise<PortalQuoteSummary[]> => {
    const response = await api.get<PortalQuoteSummary[]>(
      '/portal/orders/quotes',
      { params: { customer_no: customerNo, limit } },
    );
    return response.data;
  },

  /**
   * Get full quote details with all products (drill-down view)
   */
  getQuoteDetail: async (
    quoteSourceId: number,
  ): Promise<QuoteDetailResponse> => {
    const response = await api.get<QuoteDetailResponse>(
      `/portal/orders/quotes/${quoteSourceId}`,
    );
    return response.data;
  },

  /**
   * Check whether the given (customer_no, po_number) pair already
   * matches one or more non-terminal orders. Used by the create-order
   * dialog and the order-detail PO field to surface a non-blocking
   * "you may want to adjust the existing order instead" warning.
   *
   * Returns an empty `duplicates` list when either input is blank or no
   * match exists. Pass `excludeOrderId` from the order-detail modal so
   * the order being edited does not flag itself.
   */
  /**
   * Would saving this customer_no / po_number leave the PO the draft was
   * generated from? Read-only; the modal shows the answer as a question
   * (kanban 3019f212). Pass only the field being changed.
   */
  checkHeaderIdentity: async (
    orderId: string,
    patch: { customer_no?: string; po_number?: string },
  ): Promise<HeaderIdentityCheckResponse> => {
    const params: Record<string, string> = {};
    if (patch.customer_no !== undefined) params.customer_no = patch.customer_no;
    if (patch.po_number !== undefined) params.po_number = patch.po_number;
    const response = await api.get<HeaderIdentityCheckResponse>(
      `/portal/orders/${orderId}/header-identity-check`,
      { params },
    );
    return response.data;
  },

  /**
   * Record the CSR's ruling that this draft is a DIFFERENT order from the
   * one the extraction produced ("Yes, different order" in the warning).
   */
  detachSourceIdentity: async (
    orderId: string,
    body: DetachSourceIdentityRequest,
  ): Promise<OrderRecord> => {
    const response = await api.post<OrderRecord>(
      `/portal/orders/${orderId}/detach-source-identity`,
      body,
    );
    return response.data;
  },

  checkDuplicatePoOrders: async (
    customerNo: string,
    poNumber: string,
    excludeOrderId?: string,
  ): Promise<DuplicatePoCheckResponse> => {
    const params: Record<string, string> = {
      customer_no: customerNo,
      po_number: poNumber,
    };
    if (excludeOrderId) params.exclude_order_id = excludeOrderId;
    const response = await api.get<DuplicatePoCheckResponse>(
      '/portal/orders/check-duplicate-po',
      { params },
    );
    return response.data;
  },

  /**
   * Get warehouse options for dropdown
   */
  getWarehouses: async (): Promise<WarehouseOption[]> => {
    const response = await api.get<WarehouseOption[]>(
      '/portal/orders/warehouses',
    );
    return response.data;
  },

  /**
   * Get shipping carrier options for dropdown
   */
  getShippingCarriers: async (): Promise<ShippingCarrierOption[]> => {
    const response = await api.get<ShippingCarrierOption[]>(
      '/portal/orders/shipping-carriers',
    );
    return response.data;
  },

  /**
   * Get cancel/hold reason code options for the hold dropdown.
   * Active codes only by default; `includeInactive` also returns codes Sage
   * has deactivated so an order that still carries one can be labelled.
   */
  getCancelReasonCodes: async (
    opts: { includeInactive?: boolean } = {},
  ): Promise<CancelReasonCodeOption[]> => {
    const response = await api.get<CancelReasonCodeOption[]>(
      '/orders/cancel-reason-codes',
      { params: opts.includeInactive ? { include_inactive: true } : undefined },
    );
    return response.data;
  },

  /**
   * Get live freight quotes for an order from the Sage freight API.
   * Uses the order's ship-to zip code and line items.
   */
  getFreightQuotes: async (orderId: string): Promise<FreightQuotesResponse> => {
    const response = await api.post<FreightQuotesResponse>(
      `/portal/orders/${orderId}/freight-quotes`,
    );
    return response.data;
  },

  /**
   * Fetch the authoritative weight summary for an order — same source the
   * freight quote modal uses, but just the weight envelope (no carrier
   * lookup). On failure the backend returns `success: false` with a
   * `fallback_reason`; callers should render their local sum then.
   */
  getWeightSummary: async (
    orderId: string,
  ): Promise<WeightSummaryResponse> => {
    const response = await api.get<WeightSummaryResponse>(
      `/portal/orders/${orderId}/weight-summary`,
    );
    return response.data;
  },

  /**
   * Weight summary for a STAGED (unsaved) line set — the what-if counterpart
   * of {@link getWeightSummary}, used by the Edit-in-Sage modal to show how
   * an in-progress edit moves the shipment weight before anything is pushed.
   */
  getWeightPreview: async (
    orderId: string,
    lines: { item_code: string; quantity: number | null }[],
  ): Promise<WeightSummaryResponse> => {
    const response = await api.post<WeightSummaryResponse>(
      `/portal/orders/${orderId}/weight-preview`,
      { lines },
    );
    return response.data;
  },

  /**
   * Submit a draft order to Sage
   */
  submitToSage: async (
    orderId: string,
  ): Promise<{
    success: boolean;
    sage_order_no?: string;
    error_message?: string;
    http_status?: number;
    latency_ms?: number;
  }> => {
    const response = await api.post(
      `/portal/orders/${orderId}/submit-to-sage`,
    );
    return response.data;
  },
  /**
   * Apply an operator edit to a Sage-LANDED order and push it to Sage via the
   * salesOrderUpdate path. Only valid when `orderDetail.editable_in_sage` is
   * true (the order still exists as an open SO in Sage).
   */
  applySageEdit: async (
    orderId: string,
    payload: SageOrderEditPayload,
  ): Promise<{
    success: boolean;
    sage_order_no?: string;
    error_message?: string;
    http_status?: number;
    latency_ms?: number;
    // Soft advisories held the edit back (nothing applied) — show them and
    // offer "Push anyway" (re-send with acknowledge_warnings=true).
    requires_acknowledgment?: boolean;
    submission_warnings?: SubmissionFieldError[] | null;
    // Set when the hold is the staged-order guard: re-send with staged_override.
    staging?: OrderStagingStatus | null;
  }> => {
    const response = await api.post(
      `/portal/orders/${orderId}/sage-edit`,
      payload,
    );
    return response.data;
  },
  /**
   * Is the order already STAGED on the dock? Read from the sales site's
   * Staged tick; the Edit-in-Sage modal asks on open (kanban 42a2ed0a).
   */
  getStagingStatus: async (orderId: string): Promise<OrderStagingStatus> => {
    const response = await api.get(`/portal/orders/${orderId}/staging`);
    return response.data;
  },
  /**
   * The list the desk maintains of who gets the "staged order changed"
   * email (review ifevp1): shown in the override prompt, add / edit / remove
   * persist for everyone, each row can be unticked for one push.
   */
  listStagedNoticeRecipients: async (): Promise<StagedNoticeRecipient[]> => {
    const response = await api.get('/portal/staged-notice-recipients');
    return response.data;
  },
  addStagedNoticeRecipient: async (email: string, name?: string | null): Promise<StagedNoticeRecipient> => {
    const response = await api.post('/portal/staged-notice-recipients', { email, name: name ?? null });
    return response.data;
  },
  updateStagedNoticeRecipient: async (
    id: string,
    email: string,
    name?: string | null,
  ): Promise<StagedNoticeRecipient> => {
    const response = await api.patch(`/portal/staged-notice-recipients/${id}`, { email, name: name ?? null });
    return response.data;
  },
  removeStagedNoticeRecipient: async (id: string): Promise<StagedNoticeRecipient> => {
    const response = await api.delete(`/portal/staged-notice-recipients/${id}`);
    return response.data;
  },
  /**
   * Create a NEW Sage ship-to address under the order's customer (Edit-in-Sage
   * "new address" flow). The address is queued to Sage via the shipTo buffer;
   * customer_no is derived server-side from the order.
   */
  createSageShipTo: async (
    orderId: string,
    payload: SageShipToCreatePayload,
  ): Promise<SageShipToCreateResponse> => {
    const response = await api.post(
      `/portal/orders/${orderId}/sage-ship-to`,
      payload,
    );
    return response.data;
  },
  /**
   * Customer-scoped twin of createSageShipTo for surfaces where no order
   * exists yet (the manual order-create dialog): same validation contract,
   * same audited sage_writes path, just no order linkage.
   */
  createCustomerSageShipTo: async (
    customerNo: string,
    payload: SageShipToCreatePayload,
  ): Promise<SageShipToCreateResponse> => {
    const response = await api.post(
      `/portal/customers/${encodeURIComponent(customerNo)}/sage-ship-to`,
      payload,
    );
    return response.data;
  },
  /**
   * Edit IN PLACE a ship-to this order's draft just created (same code,
   * audited as a ship_to_update) instead of allocating a second one.
   */
  updateSageShipTo: async (
    orderId: string,
    shipToCode: string,
    payload: SageShipToCreatePayload,
  ): Promise<SageShipToCreateResponse> => {
    const response = await api.put(
      `/portal/orders/${orderId}/sage-ship-to/${encodeURIComponent(shipToCode)}`,
      payload,
    );
    return response.data;
  },
  /** The ship-to the portal most recently created/edited for this order, or null. */
  getCreatedSageShipTo: async (
    orderId: string,
  ): Promise<PortalOrderCreatedShipTo | null> => {
    const response = await api.get(
      `/portal/orders/${orderId}/sage-ship-to/created`,
    );
    return response.data ?? null;
  },
  /**
   * The customer's own Sage rep — the new-ship-to form's salesperson
   * suggestion (pre-filled, CSR-adjustable).
   */
  getCustomerSageRep: async (
    customerNo: string,
  ): Promise<CustomerSageRep> => {
    const response = await api.get(
      `/portal/customers/${encodeURIComponent(customerNo)}/sage-rep`,
    );
    return response.data;
  },
  /**
   * The order's Sage write history (audit view over sage_writes): every
   * Edit-in-Sage push and order-linked ship-to create, newest first.
   */
  getSageWrites: async (orderId: string): Promise<SageWriteAuditEntry[]> => {
    const response = await api.get(
      `/portal/orders/${orderId}/sage-writes`,
    );
    return response.data;
  },
  /**
   * Confirm a Merlin edit proposal — the ONLY way it reaches Sage. Routes
   * through the audited apply_order_edit path server-side; the backend's
   * one-shot proposal_key guard makes a double confirm a 400, not a
   * double-apply.
   */
  confirmEditProposal: async (
    orderId: string,
    proposalId: string,
    edits?: OrderEditProposalEditsPayload,
  ): Promise<{
    success: boolean;
    proposal_status: string;
    sage_order_no?: string | null;
    error_message?: string | null;
    latency_ms?: number | null;
  }> => {
    const response = await api.post(
      `/portal/orders/${orderId}/edit-proposals/${proposalId}/confirm`,
      edits ? { edits } : undefined,
    );
    return response.data;
  },
  /**
   * Decline a Merlin edit proposal (terminal; nothing applied). The reason is
   * optional; when given it is recorded on the proposal (learning-loop seed).
   */
  dismissEditProposal: async (
    orderId: string,
    proposalId: string,
    reason?: string,
  ): Promise<{ success: boolean; proposal_status: string; dismiss_reason?: string | null }> => {
    const response = await api.post(
      `/portal/orders/${orderId}/edit-proposals/${proposalId}/dismiss`,
      { reason: reason?.trim() || null },
    );
    return response.data;
  },
  /**
   * Get order timeline (aggregated lifecycle events)
   */
  getOrderTimeline: async (
    orderId: string,
  ): Promise<OrderTimelineResponse> => {
    const response = await api.get(
      `/portal/orders/${orderId}/timeline`,
    );
    return response.data;
  },

  /**
   * Batch-fetch the latest timeline event for multiple orders.
   */
  getLatestEvents: async (
    orderIds: string[],
  ): Promise<LatestEventsResponse> => {
    const params = new URLSearchParams();
    orderIds.forEach((id) => params.append('order_ids', id));
    const response = await api.get(
      `/portal/orders/latest-events?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * Read both Sage note fields (production + customer-service) for an order.
   */
  getSageNotes: async (orderId: string): Promise<SageOrderNotes> => {
    const response = await api.get<SageOrderNotes>(
      `/portal/orders/${orderId}/sage-notes`,
    );
    return response.data;
  },

  /**
   * Queue a dated customer-service note append for Sage sync.
   */
  saveCsNotes: async (
    orderId: string,
    csNotes: string,
  ): Promise<CsNotesUpdateResponse> => {
    const response = await api.put<CsNotesUpdateResponse>(
      `/portal/orders/${orderId}/sage-notes`,
      { cs_notes: csNotes },
    );
    return response.data;
  },

  archiveCsNote: async (
    orderId: string,
    entryId: string,
  ): Promise<CsNotesUpdateResponse> => {
    const response = await api.delete<CsNotesUpdateResponse>(
      `/portal/orders/${orderId}/sage-notes/${entryId}`,
    );
    return response.data;
  },

  /**
   * Fetch per-line Sage comments (read-only) for an order. Returns
   * `available: false` for drafts and orders not yet synced from Sage.
   */
  getLineComments: async (orderId: string): Promise<OrderLineComments> => {
    const response = await api.get<OrderLineComments>(
      `/portal/orders/${orderId}/line-comments`,
    );
    return response.data;
  },

  /** Order-level instructions (Merlin-extracted + CSR-added). */
  getInstructions: async (orderId: string): Promise<InstructionListResponse> => {
    const response = await api.get<InstructionListResponse>(
      `/portal/orders/${orderId}/instructions`,
    );
    return response.data;
  },

  /** Add one instruction; on a submitted order it is pushed to Sage now. */
  addInstruction: async (
    orderId: string,
    text: string,
  ): Promise<InstructionMutationResponse> => {
    const response = await api.post<InstructionMutationResponse>(
      `/portal/orders/${orderId}/instructions`,
      { text },
    );
    return response.data;
  },

  /** Edit a still-pending instruction's text. */
  updateInstruction: async (
    orderId: string,
    instructionId: string,
    text: string,
  ): Promise<InstructionMutationResponse> => {
    const response = await api.patch<InstructionMutationResponse>(
      `/portal/orders/${orderId}/instructions/${instructionId}`,
      { text },
    );
    return response.data;
  },

  /** Remove a still-pending instruction (soft — the row stays audited). */
  deleteInstruction: async (
    orderId: string,
    instructionId: string,
  ): Promise<InstructionMutationResponse> => {
    const response = await api.delete<InstructionMutationResponse>(
      `/portal/orders/${orderId}/instructions/${instructionId}`,
    );
    return response.data;
  },

  /** Accept one suggested (Merlin) instruction into the Sage-bound set. */
  acceptInstruction: async (
    orderId: string,
    instructionId: string,
  ): Promise<InstructionMutationResponse> => {
    const response = await api.post<InstructionMutationResponse>(
      `/portal/orders/${orderId}/instructions/${instructionId}/accept`,
    );
    return response.data;
  },

  /** Accept every suggested instruction on the order at once. */
  acceptAllInstructions: async (
    orderId: string,
  ): Promise<InstructionMutationResponse> => {
    const response = await api.post<InstructionMutationResponse>(
      `/portal/orders/${orderId}/instructions/accept-all`,
    );
    return response.data;
  },

  /**
   * The submit gate's ruling on every suggested (Merlin) instruction:
   * `accept` rows become Sage-bound, `skip` rows are left off on the record.
   */
  decideInstructions: async (
    orderId: string,
    decision: { accept: string[]; skip: string[]; skip_context?: InstructionRemovalContext },
  ): Promise<InstructionMutationResponse> => {
    const response = await api.post<InstructionMutationResponse>(
      `/portal/orders/${orderId}/instructions/decide`,
      decision,
    );
    return response.data;
  },

  /** Deliver pending instructions to the live Sage order now (retry). */
  pushInstructions: async (
    orderId: string,
  ): Promise<InstructionMutationResponse> => {
    const response = await api.post<InstructionMutationResponse>(
      `/portal/orders/${orderId}/instructions/push`,
    );
    return response.data;
  },

  /**
   * Set whether the order bills freight to the customer at invoicing.
   * Waiving keeps a DO NOT BILL FREIGHT /C instruction on the Sage order;
   * the flag locks once Sage has invoiced the order.
   */
  setChargeFreight: async (
    orderId: string,
    chargeFreight: boolean,
  ): Promise<ChargeFreightUpdateResponse> => {
    const response = await api.patch<ChargeFreightUpdateResponse>(
      `/portal/orders/${orderId}/charge-freight`,
      { charge_freight: chargeFreight },
    );
    return response.data;
  },

  /** Where the order's hold stands between the portal and Sage. */
  getHoldStatus: async (orderId: string): Promise<OrderHoldStatus> => {
    const response = await api.get<OrderHoldStatus>(`/portal/orders/${orderId}/hold`);
    return response.data;
  },

  /**
   * Set/change the hold (a code) or RELEASE it (null) on an order. Drafts are
   * updated locally; submitted orders write to live Sage (release puts an
   * explicit empty CancelReasonCode on the wire).
   */
  setHold: async (
    orderId: string,
    cancelReasonCode: string | null,
    note?: string,
  ): Promise<OrderHoldChangeResponse> => {
    const response = await api.put<OrderHoldChangeResponse>(
      `/portal/orders/${orderId}/hold`,
      { cancel_reason_code: cancelReasonCode, note: note ?? null },
    );
    return response.data;
  },

  /**
   * Park a draft the desk is deliberately leaving un-keyed (reason + short
   * note). Answers the open critical alerts on the order and keeps it in the
   * drafts list as a reminder.
   */
  parkOrder: async (
    orderId: string,
    reason: DraftParkReason,
    note?: string | null,
    until?: string | null,
  ): Promise<OrderParkChangeResponse> => {
    const response = await api.post<OrderParkChangeResponse>(
      `/portal/orders/${orderId}/park`,
      { reason, note: note ?? null, until: until ?? null },
    );
    return response.data;
  },

  /** Remove the parked marker; the desk is working the draft again. */
  unparkOrder: async (orderId: string): Promise<OrderParkChangeResponse> => {
    const response = await api.post<OrderParkChangeResponse>(`/portal/orders/${orderId}/unpark`);
    return response.data;
  },

  /**
   * Set or edit the truckload freight amount (freight + fuel surcharge) on
   * an order. Truckload/logistics carriers only; drafts and orders open in
   * Sage; locked once invoiced. Every call appends a history row, and when
   * the order already has a sage_order_no the amount is pushed to the
   * sales site (the response's `push` reports how that went).
   */
  setTruckloadFreight: async (
    orderId: string,
    body: TruckloadFreightUpdateRequest,
  ): Promise<TruckloadFreightUpdateResponse> => {
    const response = await api.patch<TruckloadFreightUpdateResponse>(
      `/portal/orders/${orderId}/truckload-freight`,
      body,
    );
    return response.data;
  },

  /** Answer the scanned BOL that is waiting on the desk (PRO, ship without one, confirm shipped). */
  answerBolScanDeskAsk: async (
    orderId: string,
    body: BolScanDeskAnswer,
  ): Promise<BolScanResubmitInfo> => {
    const response = await api.post<BolScanResubmitInfo>(
      `/portal/orders/${orderId}/bol-scan/answer`,
      body,
    );
    return response.data;
  },

  /** Every truckload freight version ever set on the order, newest first. */
  getTruckloadFreightHistory: async (
    orderId: string,
  ): Promise<TruckloadFreightHistoryEntry[]> => {
    const response = await api.get<{ entries: TruckloadFreightHistoryEntry[] }>(
      `/portal/orders/${orderId}/truckload-freight/history`,
    );
    return response.data.entries;
  },

  /**
   * The order's explicit /SURCHG line: what it carries now, the portal's 5%
   * suggestion (with the goods base it came from), the account's default
   * status and whether a change can be applied (kanban 51aaddc5).
   */
  getSurcharge: async (orderId: string): Promise<SurchargePreview> => {
    const response = await api.get<SurchargePreview>(`/portal/orders/${orderId}/surcharge`);
    return response.data;
  },

  /**
   * Set the order's /SURCHG line. Drafts change locally (the line rides the
   * submission); orders open in Sage go through Edit-in-Sage. The response
   * carries the full refreshed line set + totals so the modal re-renders
   * without a reload.
   */
  setSurcharge: async (
    orderId: string,
    body: SurchargeSetRequest,
  ): Promise<SurchargeSetResponse> => {
    const response = await api.put<SurchargeSetResponse>(
      `/portal/orders/${orderId}/surcharge`,
      body,
    );
    return response.data;
  },

  /** Merlin's current recommendations on the order (kanban 8d83c042). */
  getRecommendations: async (orderId: string): Promise<OrderRecommendation[]> => {
    const response = await api.get<OrderRecommendation[]>(`/portal/orders/${orderId}/recommendations`);
    return response.data;
  },

  /**
   * The CSR's click on a recommendation: accept writes the recommended
   * /FREIGHTDIS line, edit writes the keyed amount, ignore dismisses, reopen
   * brings an ignored one back. 409 carries the reason when the line must
   * not be written (invoiced, deal applies, over freight, Sage refused).
   */
  decideRecommendation: async (
    orderId: string,
    recommendationId: string,
    body: RecommendationDecisionRequest,
  ): Promise<RecommendationDecisionResponse> => {
    const response = await api.post<RecommendationDecisionResponse>(
      `/portal/orders/${orderId}/recommendations/${recommendationId}/decide`,
      body,
    );
    return response.data;
  },
};

export default portalOrderService;
